import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import UpcomingMatchesService from "../services/matchService";
import { AuthContext } from "../store/ContextStore";
import { useContext } from "react";


interface Match {
  g_match_id: string | number; // required
  match_status?: string;        // optional
  team_score?: number;
  opponent_score?: number;
  match_time?: string;
  opponent_logo?: string;
  opponent_team_name?: string;
  home?: number;
  venue_name?: string;
}

const FullSchedule = () => {
  const { selectedSport } = useContext(AuthContext);

  const { teamName, season } = useParams<{
    teamName: string;
    season: string;
  }>();
  const [schedule, setSchedule] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [teamCode, setTeamCode] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null); 

  const navigate = useNavigate();
  const upcomingMatchesService = new UpcomingMatchesService();

  useEffect(() => {
    // Get team code from localStorage
    const data = localStorage.getItem("sid_user");
    if (data) {
      try {
        const parsedData = JSON.parse(data);
        setTeamCode(parsedData.team?.code || null);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!teamCode || !season) {
        setLoading(false);
        return;
      }
      try {
        const data = await upcomingMatchesService.getFullSchedule(
          teamCode,
          season,
          selectedSport
        );
        console.log("data is ",data)
        // ✅ Handle both array (schedule) and object (message)
        if (Array.isArray(data)) {
          setSchedule(data);
          setMessage(null);
        } else if (data?.message) {
          setSchedule([]);
          setMessage(data.message);
        } else {
          setSchedule([]);
          setMessage("No schedule data available");
        }
      } catch (error) {
        console.error("Error fetching schedule:", error);
        setMessage("Error fetching schedule");
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [teamCode, season, selectedSport]);

  if (loading) {
    return (
      <p className="text-center text-gray-600 mt-10">Loading schedule...</p>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {teamName} - {season} Full Schedule
      </h1>

      {/* ✅ Show message if backend sends one */}
      {message && (
        <p className="text-center text-gray-500 italic mb-6">{message}</p>
      )}

      {/* Cards container - NO SCROLLBAR */}
      <div className="space-y-3">
        {schedule.map((match, idx) => {
          const isCompleted = match.match_status === "COMPLETE";
          const didWin = isCompleted && match.team_score > match.opponent_score;

          const matchDate = new Date(match.match_time);

          return (
            <div
              key={match.g_match_id}
              className={`bg-white border border-gray-200 rounded-lg shadow-sm p-4 transition-all hover:shadow-md ${
                isCompleted ? "cursor-pointer hover:bg-gray-50" : ""
              }`}
              onClick={() =>
                isCompleted &&
                teamCode &&
                navigate(
                  `/gamedashboard/${selectedSport}/${match.g_match_id}/${teamCode}`
                )
              }
            >
              {/* Card Content */}
              <div className="flex items-center justify-between">
                {/* Left side - Date */}
                <div className="flex flex-col items-start min-w-[150px]">
                  <div className="text-lg font-bold text-gray-800">
                    {matchDate.toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {matchDate.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    UTC
                  </div>
                </div>

                {/* Center - Teams */}
                <div className="flex items-center flex-1">
                  <img
                    src={match.opponent_logo}
                    alt={match.opponent_team_name}
                    className="w-16 h-16 object-contain mr-6"
                  />
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {match.home === 1 ? "vs " : "at "}
                      {match.opponent_team_name}
                    </div>
                    <div className="text-lg text-gray-600">
                      {match.venue_name}
                    </div>
                  </div>
                </div>

                {/* Right side - Score */}
                <div className="flex items-center min-w-[200px] justify-end">
                  {isCompleted ? (
                    <div className="text-right">
                      <div className="text-4xl font-bold text-gray-800">
                        {match.team_score} - {match.opponent_score}
                      </div>
                      <div
                        className={`text-2xl font-bold ${
                          didWin ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {didWin ? "WIN" : "LOSS"}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xl text-gray-500 font-medium">
                      Upcoming
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FullSchedule;
